import UserService from '@/services/user-service';
import { useCrossTabSessionNoticeStore } from '@/store/use-cross-tab-session-notice-store';
import { useAuthStore } from '@/store/use-auth-store';
import {
  USER_INFO_STORAGE_KEY,
  useUserStore,
} from '@/store/use-user-store';
import { AuthMeResponse } from '@/utils/auth-me';
import { BACKEND_SESSION_VALIDATED_KEY } from '@/utils/auth-session';
import { getStoredBackendUserId } from '@/utils/sync-auth-session';
import { useEffect, useRef } from 'react';

function isAuthStorageLoggedOut(value: string | null): boolean {
  if (!value) {
    return true;
  }

  try {
    const parsed = JSON.parse(value) as {
      state?: { isAuthenticated?: boolean };
    };
    return parsed.state?.isAuthenticated !== true;
  } catch {
    return false;
  }
}

function parseStoredUserIdentity(value: string | null): {
  id: number | null;
  email: string | null;
} {
  if (!value) {
    return { id: null, email: null };
  }

  try {
    const parsed = JSON.parse(value) as {
      state?: {
        user?: { id?: number | string; email?: string };
      };
    };
    const id = parsed.state?.user?.id;
    return {
      id: id != null ? Number(id) : null,
      email: parsed.state?.user?.email ?? null,
    };
  } catch {
    return { id: null, email: null };
  }
}

function openUserChangedNotice(
  openNotice: ReturnType<
    typeof useCrossTabSessionNoticeStore.getState
  >['open'],
  previousUserId: number | null,
  newUserId: number | null,
  newUserEmail: string | null
): void {
  // Login / logged-out tabs are not in an active session — no refresh prompt.
  if (!useAuthStore.getState().isAuthenticated) {
    return;
  }

  if (
    previousUserId == null ||
    newUserId == null ||
    previousUserId === newUserId
  ) {
    return;
  }

  openNotice('user_changed', newUserEmail);
}

/**
 * Detects sign-in / sign-out in another tab and prompts the user to refresh
 * so cookie, UI, and permissions stay aligned.
 */
export function useCrossTabSessionNotice(): void {
  const openNotice = useCrossTabSessionNoticeStore((state) => state.open);
  const trackedUserIdRef = useRef<number | null>(getStoredBackendUserId());

  useEffect(() => {
    const syncTrackedUserId = () => {
      trackedUserIdRef.current = useAuthStore.getState().isAuthenticated
        ? getStoredBackendUserId()
        : null;
    };

    syncTrackedUserId();

    const unsubUser = useUserStore.subscribe(syncTrackedUserId);
    const unsubAuth = useAuthStore.subscribe(syncTrackedUserId);

    return () => {
      unsubUser();
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === USER_INFO_STORAGE_KEY) {
        if (!event.newValue) {
          if (useAuthStore.getState().isAuthenticated) {
            openNotice('signed_out');
          }
          return;
        }

        const previousUserId = trackedUserIdRef.current;
        const { id: newUserId, email: newUserEmail } = parseStoredUserIdentity(
          event.newValue
        );
        openUserChangedNotice(
          openNotice,
          previousUserId,
          newUserId,
          newUserEmail
        );
        return;
      }

      if (event.key === BACKEND_SESSION_VALIDATED_KEY && event.newValue) {
        const previousUserId = trackedUserIdRef.current;

        void UserService.getCurrentUser({ skipUnauthorizedRedirect: true })
          .then((rawData) => {
            const data = rawData as AuthMeResponse;
            openUserChangedNotice(
              openNotice,
              previousUserId,
              data.user?.id ?? null,
              data.user?.email ?? null
            );
          })
          .catch(() => {});
        return;
      }

      if (
        event.key === 'auth-storage' &&
        isAuthStorageLoggedOut(event.newValue)
      ) {
        if (useAuthStore.getState().isAuthenticated) {
          openNotice('signed_out');
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [openNotice]);
}
